import { type Finplan, type FinplanRow, type FinplanSettings } from '@fin/shared';


import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, ErrorBox, Icon, Loading, Modal, NumberInput, Section, cx, toast, useAction } from '../../components/ui.tsx';
import { authStore, put } from '../../lib/api.ts';

import { dateLong, monthName, numInput, parseNum, pct, rub } from '../../lib/format.ts';
import { invalidate, useQuery } from '../../lib/query.ts';
import { Link } from '../../lib/router.tsx';
import { BrokerSheet } from '../analytics/public.ts';
import { CapitalChart } from './chart.tsx';

export const settingFields: { key: keyof FinplanSettings; label: string; percent?: boolean }[] = [
  { key: 'startCapital', label: 'Стартовый капитал' },
  { key: 'monthlyIncome', label: 'Доход в месяц сейчас' },
  { key: 'monthlyExpenses', label: 'Расходы в месяц сейчас' },
  { key: 'incomeGrowth', label: 'Рост дохода в год', percent: true },
  { key: 'investRate', label: 'Доходность инвестиций', percent: true },
  { key: 'inflation', label: 'Инфляция', percent: true },
  { key: 'ageInFirstYear', label: 'Возраст в первый год' },
  { key: 'firstYear', label: 'Первый год' },
  { key: 'baseYear', label: 'Последний год с фактом' },
  { key: 'lastYear', label: 'Горизонт, год' },
];

export function FinplanSection({ year, onYear }: { year: number | null; onYear: (y: number) => void }) {
  const q = useQuery<Finplan>('/finplan');
  const [open, setOpen] = useState(false);
  const [broker, setBroker] = useState(false);
  const ai = !!authStore.get().user?.ai;
  if (q.error && !q.data) return <ErrorBox error={q.error} onRetry={q.reload} />;
  if (!q.data) return <Loading />;
  const fp = q.data;
  // the broker's value of the last actual year replaces the investment wallets' balance
  const market = fp.years.find((y) => y.year === fp.settings.baseYear && y.marketValue != null);

  return (
    <>
      <Section title="Финансовый план" className="finplan"
        aside={<>
          {ai ? <Button size="sm" onClick={() => setBroker(true)}><Icon name="briefcase" size={16} /> Брокер</Button> : null}
          <Button size="sm" onClick={() => setOpen(true)}><Icon name="sliders" size={16} /> Параметры</Button>
        </>}>
        <CapitalChart rows={fp.rows} year={year} onYear={onYear} />
      </Section>

      <Section title="Прогноз по годам" aside={<span className="muted small">До {fp.settings.baseYear} года включительно — факт: доходы и расходы из операций{market ? <>, капитал {market.year} года по данным брокера (<span className="money">{rub(market.marketValue)}</span>{market.marketDate ? ` на ${dateLong(market.marketDate)}` : ''})</> : fp.actuals?.capitalNow != null ? <>, капитал от текущего остатка инвестиций (<span className="money">{rub(fp.actuals.capitalNow)}</span>)</> : ', капитал по пополнениям'}{fp.actuals?.monthsLeft ? `; оставшиеся ${fp.actuals.monthsLeft} мес. ${fp.settings.baseYear} года — прогноз по параметрам` : ''}</span>}>
        <YearTable fp={fp} />
      </Section>

      <Section title="Пополнения по месяцам" aside={<span className="muted small">по операциям</span>}>
        <p className="muted small">Считается автоматически: операции в категориях с пометкой «инвестиции» и переводы на кошельки типа «Инвестиции». Вывод средств уменьшает сумму месяца. Отметка категории — в настройках.</p>
        <ContributionsGrid fp={fp} />
      </Section>
      {open ? <FinplanSettingsModal fp={fp} onClose={() => setOpen(false)} /> : null}
      {broker ? <BrokerSheet onClose={() => setBroker(false)} /> : null}
    </>
  );
}

export function FinplanSettingsModal({ fp, onClose }: { fp: Finplan; onClose: () => void }) {
  const [settings, setSettings] = useState<FinplanSettings>(fp.settings);
  const { busy, run } = useAction();
  const dirty = JSON.stringify(settings) !== JSON.stringify(fp.settings);
  const save = () => run(async () => {
    await put<Finplan>('/finplan/settings', settings);
    invalidate('/finplan');
    onClose();
  }, 'Параметры сохранены');
  return (
    <Modal className="modal-form" title="Параметры финплана" onClose={onClose} footer={<>
      <span />
      <div className="row-gap"><Button onClick={onClose}>Отмена</Button><Button variant="primary" busy={busy} disabled={!dirty} onClick={save}>Пересчитать</Button></div>
    </>}>
      <form className="params params-modal" onSubmit={(e) => { e.preventDefault(); void save(); }}>
        {settingFields.map((f) => (
          <label key={f.key} className="param">
            <span className="param-label">{f.label}</span>
            <NumberInput value={f.percent ? Math.round(settings[f.key] * 1000) / 10 : settings[f.key]}
              onChange={(v) => setSettings({ ...settings, [f.key]: f.percent ? (v ?? 0) / 100 : v ?? 0 })}
              aria-label={f.label} />
            {f.percent ? <span className="param-unit">%</span> : null}
          </label>
        ))}
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}

export const pretty = (v: number | null) => (v === null ? '' : v.toLocaleString('ru-RU', { maximumFractionDigits: 2 }));

export function CellInput({ value, onCommit, label }: { value: number | null; onCommit: (v: number | null) => Promise<unknown>; label: string }) {
  const [text, setText] = useState(pretty(value));
  const committed = useRef(value);
  useEffect(() => { setText(pretty(value)); committed.current = value; }, [value]);
  return (
    <input className="cell-input" inputMode="decimal" value={text} aria-label={label} placeholder="—"
      onFocus={() => setText(numInput(value))}
      onChange={(e) => setText(e.target.value)}
      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
      onBlur={async () => {
        const v = parseNum(text);
        if (v === committed.current) { setText(pretty(v)); return; }
        const prev = committed.current;
        committed.current = v;
        try { await onCommit(v); } catch (err) { committed.current = prev; toast((err as Error).message, 'error'); setText(pretty(prev)); }
      }} />
  );
}

export function YearTable({ fp }: { fp: Finplan }) {
  const r = fp.rows;
  const now = new Date().getFullYear();
  const oneTime = new Map(fp.years.map((y) => [y.year, y.oneTimeExpenses]));
  const row = (label: string, get: (x: FinplanRow) => React.ReactNode, cls?: string) => (
    <tr className={cls}><th scope="row">{label}</th>{r.map((x) => <td key={x.year} className={cx(x.isActual && 'actual', x.year === now && 'current')}>{get(x)}</td>)}</tr>
  );
  const n = (v: number) => Math.round(v).toLocaleString('ru-RU');
  // interest is only forecast: a finished year shows its real result in «Доходность» instead
  const forecast = (x: FinplanRow) => !x.isActual || x.forecastMonths > 0;
  // the current year opens in the middle of the visible columns, right of the sticky row titles
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = box.current;
    const cur = el?.querySelector<HTMLElement>('thead th.current');
    const sticky = el?.querySelector<HTMLElement>('thead th:first-child');
    if (!el || !cur || !sticky) return;
    const free = el.clientWidth - sticky.offsetWidth;
    el.scrollLeft = Math.max(0, cur.offsetLeft + cur.offsetWidth / 2 - sticky.offsetWidth - free / 2);
  }, [r.length]);
  return (
    <div className="table-scroll" ref={box}>
      <table className="year-table">
        <thead><tr><th scope="col">Возраст</th>{r.map((x) => <th key={x.year} scope="col" className={cx(x.isActual && 'actual', x.year === now && 'current')} aria-current={x.year === now ? 'date' : undefined}>{x.age}</th>)}</tr></thead>
        <tbody>
          {row('Год', (x) => x.year, 'row-year')}
          {row('Доход в месяц', (x) => n(x.monthlyIncome))}
          {row('Расходы в месяц', (x) => n(x.monthlyExpenses))}
          {row('Дельта в месяц', (x) => n(x.monthlyDelta))}
          {row('Накоплено за год', (x) => n(x.accumulated))}
          {row('Капитал прошлого года', (x) => n(x.prevCapital))}
          {row('% на капитал прошлого года', (x) => forecast(x) ? n(x.interestOnPrev) : null)}
          {row('% на капитал текущего года', (x) => forecast(x) ? n(x.interestOnCurrent) : null)}
          {row('Доходность за год · в факте рост или падение', (x) => n(x.growth))}
          {row('Доходность, % от капитала', (x) => x.growthRate === null ? null : pct(x.growthRate, 1))}
          <tr className="row-edit"><th scope="row">Разовые крупные расходы</th>{r.map((x) => (
            <td key={x.year}><CellInput label={`Разовые расходы ${x.year}`} value={oneTime.get(x.year) || null}
              onCommit={(v) => put(`/finplan/years/${x.year}`, { oneTimeExpenses: v ?? 0, passiveRate: fp.years.find((y) => y.year === x.year)?.passiveRate ?? null }).then(() => invalidate('/finplan'))} /></td>
          ))}</tr>
          {row('Итоговый капитал', (x) => n(x.totalCapital), 'row-total')}
          {row('Ставка на пассивный доход · доходность − инфляция', (x) => pct(x.passiveRate, 1))}
          {row('Пассивный доход в год', (x) => n(x.passiveYear))}
          {row('Пассивный доход в месяц', (x) => n(x.passiveMonth), 'row-passive')}
        </tbody>
      </table>
    </div>
  );
}

export function ContributionsGrid({ fp }: { fp: Finplan }) {
  const years = useMemo(() => {
    const ys: number[] = [];
    for (let y = fp.settings.firstYear; y <= Math.max(fp.settings.baseYear, fp.settings.firstYear); y++) ys.push(y);
    return ys;
  }, [fp.settings]);
  const val = (y: number, m: number) => fp.contributions.find((c) => c.year === y && c.month === m)?.amount ?? 0;
  const sum = (y: number) => fp.contributions.filter((c) => c.year === y).reduce((s, c) => s + c.amount, 0);
  const n = (v: number) => Math.round(v).toLocaleString('ru-RU');
  const pad = (v: number) => String(v).padStart(2, '0');
  return (
    <div className="table-scroll">
      <table className="grid-table">
        <thead><tr><th scope="col">Месяц</th>{years.map((y) => <th key={y} scope="col">{y}</th>)}</tr></thead>
        <tbody>
          {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
            <tr key={m}>
              <th scope="row">{monthName(m - 1)}</th>
              {years.map((y) => {
                const v = val(y, m);
                const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
                return (
                  <td key={y} className={cx(v === 0 && 'zero', v < 0 && 'neg')}>
                    {v === 0 ? '—' : <Link to={`/transactions?from=${y}-${pad(m)}-01&to=${y}-${pad(m)}-${pad(last)}`} className="link" style={{ fontSize: 'inherit', color: 'inherit' }}>{n(v)}</Link>}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
        <tfoot><tr><th scope="row">Сумма за год</th>{years.map((y) => <td key={y}>{rub(sum(y))}</td>)}</tr></tfoot>
      </table>
    </div>
  );
}
